
import React, { useState } from "react";

// ===== Câu sắp xếp: kéo thả (HTML5 drag & drop) =====
// order: mảng key hiện tại; onChange(newOrder); textOf(k); readOnly; dapan (đáp án đúng để hiện gợi ý khi xem lại)
function SortQuestion({ order, onChange, textOf, readOnly, dapan }) {
    const [dragKey, setDragKey] = useState(null);   // key đang kéo
    const [overKey, setOverKey] = useState(null);   // key đang hover phía trên

    const reorder = (fromKey, toKey) => {
        if (fromKey === toKey) return;
        const arr = order.slice();
        const from = arr.indexOf(fromKey);
        const to = arr.indexOf(toKey);
        if (from === -1 || to === -1) return;
        arr.splice(from, 1);
        arr.splice(to, 0, fromKey);
        onChange && onChange(arr);
    };

    // Fallback nút lên/xuống cho cảm ứng / thiết bị không kéo được
    const move = (idx, dir) => {
        const ni = idx + dir;
        if (ni < 0 || ni >= order.length) return;
        const arr = order.slice();
        [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
        onChange && onChange(arr);
    };

    return (
        <div>
            {dapan && <div className="small text-muted mb-2">Thứ tự đúng: {dapan.map(textOf).join(' → ')}</div>}
            {!readOnly && <div className="small text-muted mb-2"><span className="fas fa-hand-pointer mr-1"></span>Kéo thả các mục để sắp xếp theo đúng thứ tự.</div>}
            {order.map((k, idx) => {
                const isDragging = dragKey === k;
                const isOver = overKey === k && dragKey !== k;
                return (
                    <div
                        key={k}
                        draggable={!readOnly}
                        onDragStart={(e) => { setDragKey(k); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragEnter={(e) => { e.preventDefault(); if (!readOnly) setOverKey(k); }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); if (!readOnly && dragKey) reorder(dragKey, k); setDragKey(null); setOverKey(null); }}
                        onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                        className="d-flex align-items-center mb-2 border rounded p-2"
                        style={{
                            cursor: readOnly ? 'default' : 'grab',
                            background: isOver ? '#e7f1ff' : (isDragging ? '#f1f3f5' : '#fff'),
                            borderColor: isOver ? '#0d6efd' : '#dee2e6',
                            opacity: isDragging ? 0.6 : 1,
                            transition: 'background .12s, border-color .12s',
                        }}
                    >
                        {!readOnly && <span className="fas fa-grip-vertical text-muted mr-2" title="Kéo để di chuyển"></span>}
                        <span className="badge badge-info mr-2">{idx + 1}</span>
                        <span className="mr-auto">{textOf(k)}</span>
                        {!readOnly &&
                            <>
                                <button type="button" className="btn btn-sm btn-light" onClick={() => move(idx, -1)}><span className="fas fa-arrow-up"></span></button>
                                <button type="button" className="btn btn-sm btn-light" onClick={() => move(idx, 1)}><span className="fas fa-arrow-down"></span></button>
                            </>
                        }
                    </div>
                );
            })}
        </div>
    );
}

// Component render 1 câu hỏi cho thí sinh làm bài.
// Props:
//   cauhoi: { cauhoi_id, title, loai, data }  (data đã parse hoặc JSON string)
//   value: câu trả lời hiện tại (single: key | multiple: [key] | sort: [key] | match: {leftKey:rightKey} | essay: string)
//   onChange(newValue)
//   readOnly: bool
//   correct: đáp án đúng (chỉ truyền khi xem kết quả/chấm) — dùng tô màu
export default function CauHoiRenderer({ cauhoi, value, onChange, readOnly = false, correct = null }) {
    const parse = (v, fb) => {
        if (v === null || v === undefined || v === '') return fb;
        if (typeof v === 'object') return v;
        try { return JSON.parse(v); } catch (e) { return fb; }
    };
    const data = parse(cauhoi.data, null);
    const dapan = correct !== null ? parse(correct, null) : null;

    // ===== single =====
    if (cauhoi.loai === 'single') {
        const opts = Array.isArray(data) ? data : [];
        return (
            <div>
                {opts.map((o) => {
                    const checked = String(value) === String(o.key);
                    let cls = '';
                    if (dapan !== null) {
                        if (String(dapan) === String(o.key)) cls = 'text-success font-weight-bold';
                        else if (checked) cls = 'text-danger';
                    }
                    return (
                        <div className={"mb-2 " + cls} key={o.key}>
                            <label className="d-flex align-items-center mb-0" style={{ cursor: readOnly ? 'default' : 'pointer', paddingLeft: 0 }}>
                                <input type="radio" name={"ch_" + cauhoi.cauhoi_id} className="mr-2" style={{ position: 'static', opacity: 1, width: 'auto', height: 'auto' }} disabled={readOnly}
                                    checked={checked} onChange={() => onChange && onChange(o.key)} />
                                <span>{o.text}</span>
                                {dapan !== null && String(dapan) === String(o.key) && <span className="fas fa-check ml-2 text-success"></span>}
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    }

    // ===== multiple =====
    if (cauhoi.loai === 'multiple') {
        const opts = Array.isArray(data) ? data : [];
        const arr = Array.isArray(value) ? value.map(String) : [];
        const da = Array.isArray(dapan) ? dapan.map(String) : null;
        const toggle = (key) => {
            if (readOnly) return;
            const k = String(key);
            const next = arr.includes(k) ? arr.filter(x => x !== k) : [...arr, k];
            onChange && onChange(next);
        };
        return (
            <div>
                {opts.map((o) => {
                    const checked = arr.includes(String(o.key));
                    let cls = '';
                    if (da !== null) {
                        if (da.includes(String(o.key))) cls = 'text-success font-weight-bold';
                        else if (checked) cls = 'text-danger';
                    }
                    return (
                        <div className={"mb-2 " + cls} key={o.key}>
                            <label className="d-flex align-items-center mb-0" style={{ cursor: readOnly ? 'default' : 'pointer', paddingLeft: 0 }}>
                                <input type="checkbox" className="mr-2" style={{ position: 'static', opacity: 1, width: 'auto', height: 'auto' }} disabled={readOnly}
                                    checked={checked} onChange={() => toggle(o.key)} />
                                <span>{o.text}</span>
                                {da !== null && da.includes(String(o.key)) && <span className="fas fa-check ml-2 text-success"></span>}
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    }

    // ===== sort ===== (thí sinh sắp thứ tự bằng kéo thả)
    if (cauhoi.loai === 'sort') {
        const opts = Array.isArray(data) ? data : [];
        // value = mảng key theo thứ tự thí sinh chọn; mặc định = thứ tự gốc data
        const order = Array.isArray(value) && value.length === opts.length ? value.slice() : opts.map(o => o.key);
        const textOf = (k) => { const f = opts.find(o => String(o.key) === String(k)); return f ? f.text : k; };
        return (
            <SortQuestion order={order} onChange={onChange} textOf={textOf} readOnly={readOnly} dapan={dapan} />
        );
    }

    // ===== match ===== (nối cột trái với cột phải)
    if (cauhoi.loai === 'match') {
        const left = data && Array.isArray(data.left) ? data.left : [];
        const right = data && Array.isArray(data.right) ? data.right : [];
        const map = value && typeof value === 'object' ? value : {};
        const da = dapan && typeof dapan === 'object' ? dapan : null;
        const setPair = (lk, rk) => { if (readOnly) return; onChange && onChange({ ...map, [lk]: rk }); };
        const rightText = (rk) => { const f = right.find(r => String(r.key) === String(rk)); return f ? f.text : ''; };
        return (
            <div>
                {left.map((l) => {
                    const chosen = map[l.key] || '';
                    let cls = '';
                    if (da !== null) {
                        cls = String(da[l.key]) === String(chosen) ? 'text-success' : 'text-danger';
                    }
                    return (
                        <div className={"d-flex align-items-center mb-2 " + cls} key={l.key}>
                            <span style={{ flex: 2 }}>{l.text}</span>
                            <span className="mx-2">→</span>
                            <select className="form-select height32" style={{ flex: 2 }} disabled={readOnly}
                                value={chosen} onChange={(e) => setPair(l.key, e.target.value)}>
                                <option value="">-- Chọn --</option>
                                {right.map((r) => <option key={r.key} value={r.key}>{r.text}</option>)}
                            </select>
                            {da !== null && <span className="ml-2 small">Đúng: {rightText(da[l.key])}</span>}
                        </div>
                    );
                })}
            </div>
        );
    }

    // ===== essay =====
    if (cauhoi.loai === 'essay') {
        return (
            <textarea className="form-control" rows={5} disabled={readOnly}
                placeholder="Nhập câu trả lời của bạn..."
                value={value || ''} onChange={(e) => onChange && onChange(e.target.value)} />
        );
    }

    return <div className="text-muted">Loại câu hỏi không hỗ trợ: {cauhoi.loai}</div>;
}
